import React, { useState, useEffect, useMemo } from 'react'; 
import { useNavigate } from 'react-router-dom';
import SubTableV2 from './SubTableV2';
import MobileHeader from './MobileHeader';
import { useTheme } from './ThemeContext';
import Spinner from './Spinner';
import { useDispatch, useSelector } from 'react-redux';
import CardGroupsAddCardModal from './CardGroupsAddCardModal';
import AddUserModal from './AddUserModal';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import {
  updateCardGroupData,
  selectCardGroupData,
  addCardsToGroup,
  setCardGroupData,
  removeCardFromGroup,
  addUserTenantMappings,
  removeUserTenantMapping
} from './cardGroupSlice';

const EditCardGroup = () => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cardGroup = useSelector(selectCardGroupData);
  
  // Original local states
  const [cardDataOriginal, setCardDataOriginal] = useState(cardGroup.cardSummaries || []);
  const [userTenantMappings, setUserTenantMappings] = useState(cardGroup.userTenantMappings || []);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [cardGroupNumber, setCardGroupNumber] = useState(cardGroup?.cardGroup?.cardGroupID || '');
  const [policyNumber, setPolicyNumber] = useState(
    cardGroup?.cardGroup ? String(cardGroup.cardGroup.PolicyNumber) : ''
  );
  const [subfleetId, setSubfleetId] = useState(cardGroup?.cardGroup?.SubfleetId || '');
  const [successMessage, setSuccessMessage] = useState('');
  const [cardTableAlert, setCardTableAlert] = useState('');
  const [userTableAlert, setUserTableAlert] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [removalType, setRemovalType] = useState(null);
  const [removalIdentifier, setRemovalIdentifier] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);

  const [discountGroups, setDiscountGroups] = useState([]); 
  const [selectedDiscountGroup, setSelectedDiscountGroup] = useState(
    cardGroup?.cardGroup?.DiscountGroupId || ''
  );

  // Account type logic
  // We interpret isPrePay from the DB as a boolean. We'll map it to 'prepay' or 'credit' for UI.
  const [accountType, setAccountType] = useState(
    cardGroup?.cardGroup?.isPrePay ? 'prepay' : 'credit'
  );

  // If we choose credit, we can store a credit amount
  const [creditAmount, setCreditAmount] = useState(
    cardGroup?.cardGroup?.CreditLimit || 0
  );

  const policies = useSelector((state) => state.policies.activePolicies);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Clear messages
  const clearSuccessMessage = () => setSuccessMessage('');
  const clearAlert = () => setAlertMessage('');
  const clearCardAlert = () => setCardTableAlert('');
  const clearUserAlert = () => setUserTableAlert('');
  
  const toggleCardModal = () => setShowAddCardModal(!showAddCardModal);
  const toggleUserModal = () => setShowAddUserModal(!showAddUserModal);

  // Fetch discount groups - similar to your EditPoliciesForm approach
  useEffect(() => {
    const fetchDiscountGroups = async () => {
      try {
        const response = await fetch(`${apiUrl}/dataServer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'getDiscountGroups',
            payload: { userId: currentUser.userId }
          })
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setDiscountGroups(data);
        } else if (data?.discountGroups) {
          setDiscountGroups(data.discountGroups);
        }
      } catch (error) {
        console.error('Error fetching discount groups:', error);
      }
    };
    fetchDiscountGroups();
  }, [apiUrl, currentUser]);

  // Rehydrate from localStorage if needed
  useEffect(() => {
    const storedCardGroupData = localStorage.getItem('cardGroupData');
    const parsedData = storedCardGroupData ? JSON.parse(storedCardGroupData) : null;

    if (parsedData && !cardGroup.cardGroup) {
      dispatch(setCardGroupData(parsedData));
      setCardDataOriginal(parsedData.cardSummaries || []);
      if (parsedData?.cardGroup) {
        setCardGroupNumber(parsedData.cardGroup.cardGroupID || '');
        setPolicyNumber(parsedData.cardGroup.PolicyNumber || '');
        setUserTenantMappings(parsedData.userTenantMappings || []);
        setSelectedDiscountGroup(parsedData.cardGroup.DiscountGroupId || '');
        setAccountType(parsedData.cardGroup.isPrePay ? 'prepay' : 'credit');
        setCreditAmount(parsedData.cardGroup.CreditLimit || 0);
      }
    }
    setIsInitializing(false);
  }, []);

  // Keep localStorage up to date
  useEffect(() => {
    if (cardGroup?.cardGroup) {
      localStorage.setItem('cardGroupData', JSON.stringify(cardGroup));
      setCardDataOriginal(cardGroup.cardSummaries || []);
      setCardGroupNumber(cardGroup.cardGroup.cardGroupID || '');
      setPolicyNumber(cardGroup.cardGroup.PolicyNumber || '');
      setSelectedDiscountGroup(cardGroup.cardGroup.DiscountGroupId || '');
      setAccountType(cardGroup.cardGroup.isPrePay ? 'prepay' : 'credit');
      setCreditAmount(cardGroup.cardGroup.CreditLimit || 0);
    } else if (!isInitializing) {
      navigate(-1);
    }
  }, [cardGroup, isInitializing, navigate]);

  // Called when user clicks "Add Cards"
  const onCardsAdded = (newCards) => {
    const existingCardNumbers = new Set(cardDataOriginal.map((card) => card.CardNumber));
    const filteredNewCards = newCards.filter(
      (card) => !existingCardNumbers.has(card.CardNumber)
    );

    if (filteredNewCards.length > 0) {
      setCardDataOriginal([...cardDataOriginal, ...filteredNewCards]);
      dispatch(addCardsToGroup(filteredNewCards));
      setSuccessMessage('Card(s) successfully added to the group.');
    } else {
      setSuccessMessage('No new cards added. Duplicate selections were not added.');
    }
  };

  // Called for removing card/user
  const handleRemove = (type, identifier) => {
    setRemovalType(type);
    setRemovalIdentifier(identifier);
    const message =
      type === 'card'
        ? 'Are you sure you want to remove this card from the card group?'
        : 'Are you sure you want to remove this user from the card group?';
    setConfirmationMessage(message);
    setShowConfirmationModal(true);
  };

  // Confirm the removal (Card or User)
  const confirmRemoval = async () => {
    if (!removalIdentifier) return;
    setLoading(true);

    const payload =
      removalType === 'card'
        ? {
            CardNumber: removalIdentifier,
            NetworkId: cardGroup.cardGroup.NetworkId,
            UserId: currentUser.userId,
            userUserId: currentUser.userId
          }
        : {
            UserId: removalIdentifier,
            NetworkId: cardGroup.cardGroup.NetworkId,
            UserId: currentUser.userId,
            userUserId: currentUser.userId
          };

    try {
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'removeFromCardGroup', payload })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const results = await response.json();

      if (results.success === false) {
        setCardTableAlert(`${results.message}`);
      } else {
        if (removalType === 'card') {
          const updatedCardData = cardDataOriginal.filter(
            (card) => card.CardNumber !== removalIdentifier
          );
          setCardDataOriginal(updatedCardData);
          dispatch(removeCardFromGroup(removalIdentifier));
          setCardTableAlert(`Successfully removed the ${removalType} from the card group.`);
        } else {
          const updatedUserMappings = userTenantMappings.filter(
            (user) => user.UserId !== removalIdentifier
          );
          setUserTenantMappings(updatedUserMappings);
          dispatch(removeUserTenantMapping(removalIdentifier));
          setUserTableAlert(`Successfully removed the ${removalType} from the card group.`);
        }
      }
    } catch (error) {
      console.error(`Failed to remove the ${removalType}: `, error);
      setCardTableAlert(`Failed to remove the ${removalType}: ${error.message}`);
    } finally {
      setLoading(false);
      setShowConfirmationModal(false);
      setRemovalIdentifier(null);
      setRemovalType(null);
    }
  };

  // Called when new users are added
  const onUsersAdded = (newUsers) => {
    const existingUserIds = new Set(userTenantMappings.map((user) => user.UserId));
    const filteredNewUsers = newUsers.filter((user) => !existingUserIds.has(user.UserId));

    if (filteredNewUsers.some((user) => user.UserId === currentUser.userId)) {
      setUserTableAlert(
        'You cannot select yourself to be added. Use the dropdown in the header to switch accounts.'
      );
      return;
    }
    if (filteredNewUsers.length > 0) {
      setUserTenantMappings([...userTenantMappings, ...filteredNewUsers]);
      dispatch(addUserTenantMappings(filteredNewUsers));
      setUserTableAlert('Successfully added user(s) to the group.');
    } else {
      setUserTableAlert(
        'No new users were added. Duplicate selections detected and were not added.'
      );
    }
  };

  // Input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateCardGroupData({ [name]: value }));

    if (name === 'PolicyNumber') {
      setPolicyNumber(String(value));
    } else if (name === 'SubfleetId') {
      setSubfleetId(value);
    }
  };

  // For toggling account type
  const handleAccountTypeChange = (e) => {
    const val = e.target.value;
    setAccountType(val);
    // If we switch to prepay, you might want to reset creditAmount to 0
    if (val === 'prepay') {
      setCreditAmount(0);
    }
  };

  // The main submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Adjust isPrePay based on accountType
    const isPrePay = accountType === 'prepay';
    // Only pass creditAmount if not prepay
    const effectiveCreditAmount = isPrePay ? 0 : creditAmount;

    const payload = {
      cardGroupId: cardGroup.cardGroup.cardGroupID,
      groupName: cardGroup.cardGroup.groupName,
      description: cardGroup.cardGroup.description,
      policyNumber: policyNumber,
      subfleetId: subfleetId,
      userId: currentUser.userId,
      transactionType: 'EDIT',
      isPrePay: isPrePay,
      discountGroupId: selectedDiscountGroup || null,
      creditLimit: effectiveCreditAmount
    };

    try {
        const response = await fetch(`${apiUrl}/dataServer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'addOrEditCardGroup', payload })
        });
      
        if (!response.ok) {
          throw new Error(`Failed to update card group: ${response.statusText}`);
        }
      
        const responseData = await response.json();
      
        if (responseData.success === false) {
          setAlertMessage(`${responseData.message}`);
        } else {
          setAlertMessage('Successfully updated the Account!');
      
          // Update the Redux store with fresh cardGroup data from server
          if (responseData.cardGroup) {
            dispatch(setCardGroupData({
              ...cardGroup,           // keep existing structure
              cardGroup: responseData.cardGroup
            }));
          }
        }
      } catch (error) {
        setAlertMessage(`Failed to update: ${error.message}`);
        console.error('Error during card group update:', error);
      } finally {
        setLoading(false);
      }
    };

  const handleCardEdit = (cardData) => {
    navigate('/editcard', { state: { cardNumber: cardData.CardNumber } });
  };

  const cardColumns = useMemo(
    () => [
      {
        name: 'Card Number',
        selector: (row) => row.CardNumber,
        sortable: true
      },
      {
        name: 'Driver Name',
        selector: (row) => row.DriverName,
        sortable: true
      },
      {
        name: 'Driver Id',
        selector: (row) => row.DriverId,
        sortable: true,
        hide: 'sm'
      },
      {
        name: 'Card Subfleet',
        selector: (row) => row.CardSubfleet,
        sortable: true,
        hide: 'sm'
      },
      {
        name: 'Policy Subfleet',
        selector: (row) => row.PolicySubfleet,
        sortable: true,
        hide: 'sm'
      },
      {
        name: 'Actions',
        cell: (row) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleCardEdit(row)}
              className="text-gray-500 hover:text-gray-700"
              title="Edit Card"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={() => handleRemove('card', row.CardNumber)}
              className="text-red-500 hover:text-red-700"
              title="Remove Card From Group"
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          </div>
        )
      }
    ],
    []
  );

  const userTenantColumns = useMemo(
    () => [
      {
        name: 'First Name',
        selector: (row) => row.FirstName,
        sortable: true,
        wrap: true
      },
      {
        name: 'Last Name',
        selector: (row) => row.LastName,
        sortable: true,
        wrap: true
      },
      {
        name: 'Phone',
        selector: (row) => row.Phone,
        sortable: true,
        wrap: true
      },
      {
        name: 'Email',
        selector: (row) => row.Email,
        sortable: true,
        wrap: true
      },
      {
        name: 'Actions',
        cell: (row) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                navigate(`/EditUserForm/${row.RecordId}`, { state: { rowData: row } });
              }}
              className="text-gray-500 hover:text-gray-700"
              title="Edit User"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={() => handleRemove('user', row.UserId)}
              className="text-red-500 hover:text-red-700"
              title="Remove User From Group"
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          </div>
        )
      }
    ],
    []
  );

  const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-10">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
          <h3 className="text-lg font-medium text-gray-900">Confirm Removal</h3>
          <p className="text-gray-500 my-4">{message}</p>
          <div className="flex justify-end gap-4">
            <button
              className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
              onClick={onClose}
            >
              No
            </button>
            <button
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              onClick={onConfirm}
            >
              Yes
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isInitializing) {
    return <Spinner />;
  }

  return (
    <div className="mobile-container mx-auto px-4 sm:px-6 lg:px-8">
      <MobileHeader theme={theme} />
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={() => confirmRemoval(removalType, removalIdentifier)}
        message={confirmationMessage}
      />

      <div className="text-end mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-gray-700 text-sm mr-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
          Go Back
        </button>
      </div>

      {/* Account Details - existing info */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h1 className="font-bold mb-4">Account Details:</h1>
        <h3 className="text-xl font-bold mb-4">{cardGroup.cardGroup.groupName}</h3>
        <p className="text-gray-600 mb-4">{cardGroup.cardGroup.description}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Account Balance</h3>
            <p className="text-gray-600">${cardGroup.cardGroup.AccountBalance}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Credit Limit</h3>
            <p className="text-gray-600">
              $
              {cardGroup?.cardGroup?.CreditLimit
                ? cardGroup.cardGroup.CreditLimit
                : '0'}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Pre-Pay Account</h3>
            <p className="text-gray-600">{cardGroup.cardGroup.isPrePay ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Account Number</h3>
            <p className="text-gray-600">{cardGroup.cardGroup.AccountNumber}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Maintain Balance Amount</h3>
            <p className="text-gray-600">${cardGroup.cardGroup?.MinFunds || '0'}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Auto Reload</h3>
            <p className="text-gray-600">
              {cardGroup.cardGroup.MaintainBalance ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Setup Complete</h3>
            <p className="text-gray-600">
              {cardGroup.cardGroup.SetupComplete ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Subfleet ID</h3>
            <p className="text-gray-600">
              {cardGroup.cardGroup.SubfleetId || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {loading && <Spinner />}

      {/* Display alert messages */}
      {alertMessage && (
        <div
          className={`alert p-4 rounded ${
            alertMessage.startsWith('Successfully')
              ? 'bg-green-200 text-green-600'
              : 'bg-red-200 text-red-600'
          } flex justify-between items-center`}
        >
          {alertMessage}
          <button onClick={clearAlert} className="text-lg font-bold">
            ×
          </button>
        </div>
      )}

      {/* Main form */}
      <form onSubmit={handleSubmit} className="space-y-2 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            name="groupName"
            value={cardGroup?.cardGroup?.groupName || ''}
            onChange={handleInputChange}
            className="mt-1 p-2 w-full border rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <input
            type="text"
            name="description"
            value={cardGroup?.cardGroup?.description || ''}
            onChange={handleInputChange}
            className="mt-1 p-2 w-full border rounded-md"
          />
        </div>

        {currentUser.isAdmin || currentUser.isContractAdmin ? (
          <div>
            <label className="block text-sm font-medium text-gray-700">Policy Number</label>
            <select
              id="PolicyNumber"
              name="PolicyNumber"
              value={policyNumber || ''}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
              required
            >
              <option value="">Select A Policy</option>
              {policies.map((policy) => (
                <option key={policy.PolicyNumber} value={String(policy.PolicyNumber)}>
                  {policy.PolicyNumber} - {policy.Description}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700">Subfleet ID</label>
          <p className="text-xs text-gray-500 mt-1">
            The Subfleet ID is applied to transactions and used for detailed reporting. 
            All cards assigned to this account will inherit this value.
          </p>
          <input
            type="text"
            name="SubfleetId"
            value={subfleetId || ''}
            onChange={handleInputChange}
            className="mt-1 p-2 w-full border rounded-md"
            maxLength="24"
            required
          />
        </div>

        {/* New UI for Prepay vs Credit */}
        {(currentUser.isAdmin || currentUser.isContractAdmin) && (
        <>
        <div>
          <label className="block text-sm font-medium text-gray-700">Account Type</label>
          <div className="flex items-center space-x-4 mt-2">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="accountType"
                value="prepay"
                checked={accountType === 'prepay'}
                onChange={handleAccountTypeChange}
                className="form-radio h-4 w-4"
              />
              <span>Prepay</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="accountType"
                value="credit"
                checked={accountType === 'credit'}
                onChange={handleAccountTypeChange}
                className="form-radio h-4 w-4"
              />
              <span>Credit</span>
            </label>
          </div>
        </div>

        {/* CreditAmount only if accountType is credit */}
        {accountType === 'credit' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Credit Amount</label>
            <input
              type="number"
              name="creditAmount"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              className="mt-1 p-2 w-full border rounded-md"
            />
          </div>
        )}

        {/* Discount Group dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Discount Group</label>
          <p className="text-xs text-gray-500 mt-1">
            If left blank, the discount group from the policy will be applied.
          </p>
          <select
            name="discountGroup"
            value={selectedDiscountGroup}
            onChange={(e) => setSelectedDiscountGroup(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          >
            <option value="">-- Select Discount Group --</option>
            {discountGroups.map((group) => (
              <option key={group.ID} value={group.ID}>
                {group.DiscountGroupName}
              </option>
            ))}
          </select>
        </div>
        </>
        )}

        <button
          type="submit"
          disabled={loading}
          className="themed-button"
        >
          Update
        </button>
      </form>

      {successMessage && (
        <div className="alert p-4 rounded bg-green-200 text-green-600 flex justify-between items-center">
          {successMessage}
          <button onClick={clearSuccessMessage} className="text-lg font-bold">
            ×
          </button>
        </div>
      )}

      {/* Cards Table */}
      <div className="flex flex-col mb-8">
        <div className="flex justify-end mb-2">
          <button onClick={toggleCardModal} className="themed-button py-2 px-4 rounded">
            Add Cards
          </button>
        </div>
        <SubTableV2
          columns={cardColumns}
          title="Cards"
          data={cardDataOriginal}
          loading={loading}
          clearAlert={clearCardAlert}
          alertMessage={cardTableAlert}
        />
      </div>

      {/* Users Table */}
      <div className="flex flex-col mb-8">
        <div className="flex justify-end mb-2">
          <button onClick={toggleUserModal} className="themed-button py-2 px-4 rounded">
            Add Users
          </button>
        </div>
        <SubTableV2
          columns={userTenantColumns}
          title="Users"
          data={userTenantMappings}
          loading={loading}
          clearAlert={clearUserAlert}
          alertMessage={userTableAlert}
        />
      </div>

      {/* Modals for adding cards and users */}
      <CardGroupsAddCardModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        cardGroupId={cardGroupNumber}
        onCardsAdded={onCardsAdded}
      />
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        cardGroupId={cardGroupNumber}
        onUsersAdded={onUsersAdded}
      />
    </div>
  );
};

export default EditCardGroup;



// import React, { useState, useEffect, useMemo } from 'react'; 
// import { useNavigate, useLocation } from 'react-router-dom';
// import SubTableV2 from './SubTableV2';
// import MobileHeader from './MobileHeader';
// import { useTheme } from './ThemeContext';
// import Spinner from './Spinner';
// import { useDispatch, useSelector } from 'react-redux';
// import CardGroupsAddCardModal from './CardGroupsAddCardModal';
// import AddUserModal from './AddUserModal';
// import { useAuth } from './AuthContext';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faEdit, faTrashAlt, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
// import { updateCardGroupData, selectCardGroupData, addCardsToGroup, setCardGroupData, removeCardFromGroup, addUserTenantMappings, removeUserTenantMapping } from './cardGroupSlice';


// const EditCardGroup = () => {
//     const { currentUser } = useAuth();
//     const { theme } = useTheme();
//     const dispatch = useDispatch();
//     const navigate = useNavigate();
//     const cardGroup = useSelector(selectCardGroupData);
//     const [cardDataOriginal, setCardDataOriginal] = useState(cardGroup.cardSummaries || []);
//     const [userTenantMappings, setUserTenantMappings] = useState(cardGroup.userTenantMappings || []);
//     const [loading, setLoading] = useState(false);
//     const [alertMessage, setAlertMessage] = useState('');
//     const [showAddCardModal, setShowAddCardModal] = useState(false);
//     const [showAddUserModal, setShowAddUserModal] = useState(false);
//     const [cardGroupNumber, setCardGroupNumber] = useState(cardGroup && cardGroup.cardGroup ? cardGroup.cardGroup.cardGroupID : '');
//     const [policyNumber, setPolicyNumber] = useState(cardGroup && cardGroup.cardGroup ? String(cardGroup.cardGroup.PolicyNumber) : '');
//     const [subfleetId, setSubfleetId] = useState(cardGroup?.cardGroup?.SubfleetId || '');
//     const [successMessage, setSuccessMessage] = useState('');
//     const [cardTableAlert, setCardTableAlert] = useState('');
//     const [userTableAlert, setUserTableAlert] = useState('');
//     const [showConfirmationModal, setShowConfirmationModal] = useState(false);
//     const [removalType, setRemovalType] = useState(null); // 'card' or 'user'
//     const [removalIdentifier, setRemovalIdentifier] = useState(null);
//     const [confirmationMessage, setConfirmationMessage] = useState('');    
//     const [isInitializing, setIsInitializing] = useState(true);
//     const policies = useSelector(state => state.policies.activePolicies);
//     const apiUrl = process.env.REACT_APP_API_URL;

//     const clearSuccessMessage = () => setSuccessMessage('');
//     const toggleCardModal = () => setShowAddCardModal(!showAddCardModal);
//     const toggleUserModal = () => setShowAddUserModal(!showAddUserModal);

//     const clearAlert = () => setAlertMessage('');
//     const clearCardAlert = () => setCardTableAlert('');
//     const clearUserAlert = () => setUserTableAlert('');

//     useEffect(() => {
//         const storedCardGroupData = localStorage.getItem('cardGroupData');        
//         const parsedData = storedCardGroupData ? JSON.parse(storedCardGroupData) : null;
//         console.log('Parsed Data:', parsedData);

//         if (parsedData && !cardGroup.cardGroup) {
//             // console.log("Rehydrating card group data from local storage");
//             dispatch(setCardGroupData(parsedData));
//             setCardDataOriginal(parsedData.cardSummaries);
//             if (parsedData?.cardGroup) {
//                 setCardGroupNumber(parsedData.cardGroup.cardGroupID || '');
//                 setPolicyNumber(parsedData.cardGroup.PolicyNumber || '');
//                 setUserTenantMappings(parsedData.userTenantMappings || '');
//             }
//         }
//         setIsInitializing(false);
//     }, []);

//     useEffect(() => {
//         if (cardGroup && cardGroup.cardGroup) {
//             localStorage.setItem('cardGroupData', JSON.stringify(cardGroup));
//             setCardDataOriginal(cardGroup.cardSummaries);
//             if (cardGroup?.cardGroup) {
//                 setCardGroupNumber(cardGroup.cardGroup.cardGroupID || '');
//                 setPolicyNumber(cardGroup.cardGroup.PolicyNumber || '');
//             }
//         } else if (!isInitializing) {
//             // Only navigate back if initialization is complete and cardGroup is still empty
//             navigate(-1);
//         }
//     }, [cardGroup, isInitializing]);



//     const onCardsAdded = (newCards) => {
//         const existingCardNumbers = new Set(cardDataOriginal.map(card => card.CardNumber));
//         const filteredNewCards = newCards.filter(card => !existingCardNumbers.has(card.CardNumber));

//         if (filteredNewCards.length > 0) {
//             setCardDataOriginal([...cardDataOriginal, ...filteredNewCards]);
//             dispatch(addCardsToGroup(filteredNewCards));
//             setSuccessMessage('Card(s) successfully added to the group.');
//             // console.log("Updated cardData: ", [...cardDataOriginal, ...filteredNewCards]);
//         } else {
//             setSuccessMessage('No new cards added. Duplicate selections were not added.');
//         }
//     };

//     const handleRemove = (type, identifier) => {
//         setRemovalType(type);
//         setRemovalIdentifier(identifier);
//         const message = type === 'card'
//             ? 'Are you sure you want to remove this card from the card group?'
//             : 'Are you sure you want to remove this user from the card group?';
//         setConfirmationMessage(message);
//         setShowConfirmationModal(true);
//     };

//     const confirmRemoval = async () => {
//         if (!removalIdentifier) return;

//         setLoading(true);
//         const payload = removalType === 'card' ? { CardNumber: removalIdentifier, NetworkId: cardGroup.cardGroup.NetworkId, UserId: currentUser.userId, userUserId: currentUser.userId } : { UserId: removalIdentifier, NetworkId: cardGroup.cardGroup.NetworkId, UserId: currentUser.userId, userUserId: currentUser.userId };
//         // console.log('Payload: ', payload);

//         try {
//             const response = await fetch(`${apiUrl}/dataServer`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ action: 'removeFromCardGroup', payload })
//             });
//             if (!response.ok) {
//                 throw new Error(`HTTP error! status: ${response.status}`);
//             }
//             const results = await response.json();

//             if (results.success === false) {
//                 setCardTableAlert(`${results.message}`);
//             } else {
//             if (removalType === 'card') {
//                 const updatedCardData = cardDataOriginal.filter(card => card.CardNumber !== removalIdentifier);
//                 setCardDataOriginal(updatedCardData);
//                 dispatch(removeCardFromGroup(removalIdentifier));
//                 setCardTableAlert(`Successfully removed the ${removalType} from the card group.`);
//             } else {
//                 const updatedUserMappings = userTenantMappings.filter(user => user.UserId !== removalIdentifier);
//                 setUserTenantMappings(updatedUserMappings);
//                 dispatch(removeUserTenantMapping(removalIdentifier));
//                 setUserTableAlert(`Successfully removed the ${removalType} from the card group.`);
//             }
//             }
//         } catch (error) {
//             console.error(`Failed to remove the ${removalType}: `, error);
//             setCardTableAlert(`Failed to remove the ${removalType}: ${error.message}`);
//         } finally {
//             setLoading(false);
//             setShowConfirmationModal(false); // Close modal after handling
//             setRemovalIdentifier(null); // Reset identifier
//             setRemovalType(null); // Reset type
//         }
//     };

//     const onUsersAdded = (newUsers) => {
//         const existingUserIds = new Set(userTenantMappings.map(user => user.UserId));
//         const filteredNewUsers = newUsers.filter(user => !existingUserIds.has(user.UserId));

//         if (filteredNewUsers.some(user => user.UserId === currentUser.userId)) {
//             setUserTableAlert('You cannot select yourself to be added. Use the dropdwon in the header to switch accounts.');
//             return;
//         }

//         if (filteredNewUsers.length > 0) {
//             setUserTenantMappings([...userTenantMappings, ...filteredNewUsers]);
//             dispatch(addUserTenantMappings(filteredNewUsers));
//             setUserTableAlert('Successfully added user(s) to the group.');
//         } else {
//             setUserTableAlert('No new users were added. Duplicate selections detected and were not added.');
//         }
//     };

//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         dispatch(updateCardGroupData({ [name]: value }));

//         if (name === 'PolicyNumber') {
//             setPolicyNumber(String(value));
//         } else if (name === 'SubfleetId') {
//             setSubfleetId(value);
//         }
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         setLoading(true);
//         const payload = {
//             cardGroupId: cardGroup.cardGroup.cardGroupID,
//             groupName: cardGroup.cardGroup.groupName,
//             description: cardGroup.cardGroup.description,
//             policyNumber: policyNumber,
//             subfleetId: subfleetId,
//             userId: currentUser.userId,
//             transactionType: 'EDIT',
//         };
//         // console.log('Payload: ', payload);
//         try {
//             const response = await fetch(`${apiUrl}/dataServer`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ action: 'addOrEditCardGroup', payload })
//             });

//             if (!response.ok) {
//                 throw new Error(`Failed to update card group: ${response.statusText}`);
//             }

//             const responseData = await response.json();
//             if (responseData.success === false) {
//                 setAlertMessage(`${responseData.message}`);
//             } else {
//             setAlertMessage('Successfully updated the Card Group!');
//             }
//             // console.log('Update response:', responseData);
//         } catch (error) {
//             setAlertMessage(`Failed to update: ${error.message}`);
//             console.error('Error during card group update:', error);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleCardEdit = (cardData) => {
//         navigate('/editcard', { state: { cardNumber: cardData.CardNumber } });
//     };

//     const cardColumns = React.useMemo(() => [
//         {
//             name: 'Card Number',
//             selector: row => row.CardNumber,
//             sortable: true
//         },
//         {
//             name: 'Driver Name',
//             selector: row => row.DriverName,
//             sortable: true
//         },
//         {
//             name: 'Driver Id',
//             selector: row => row.DriverId,
//             sortable: true,
//             hide: 'sm'

//         },
//         {
//             name: 'Card Subfleet',
//             selector: row => row.CardSubfleet,
//             sortable: true,
//             hide: 'sm'
//         },
//         {
//             name: 'Policy Subfleet',
//             selector: row => row.PolicySubfleet,
//             sortable: true,
//             hide: 'sm'
//         },
//         {
//             name: 'Actions',
//             cell: row => (
//                 <div className="flex items-center space-x-2">
//                     <button
//                         onClick={() => handleCardEdit(row)}
//                         className="text-gray-500 hover:text-gray-700"
//                         title="Edit Card"
//                     >
//                         <FontAwesomeIcon icon={faEdit} />
//                     </button>
//                     <button
//                         onClick={() => handleRemove('card', row.CardNumber)}
//                         className="text-red-500 hover:text-red-700"
//                         title="Remove Card From Group"
//                     >
//                         <FontAwesomeIcon icon={faTrashAlt} />
//                     </button>
//                 </div>
//             )
//         },
//     ], [handleCardEdit]);

//     const handleUserEdit = (row) => {
//         // console.log("Edit User:", row);        
//     };


//     const userTenantColumns = useMemo(() => [
//         {
//             name: 'First Name',
//             selector: row => row.FirstName,
//             sortable: true,
//             wrap: true
//         },
//         {
//             name: 'Last Name',
//             selector: row => row.LastName,
//             sortable: true,
//             wrap: true
//         },
//         {
//             name: 'Phone',
//             selector: row => row.Phone,
//             sortable: true,
//             wrap: true
//         },
//         {
//             name: 'Email',
//             selector: row => row.Email,
//             sortable: true,
//             wrap: true
//         },
//         {
//             name: 'Actions',
//             cell: row => (
//                 <div className="flex items-center space-x-2">
//                     <button
//                         onClick={() => {
//                             navigate(`/EditUserForm/${row.RecordId}`, { state: { rowData: row } });
//                         }}
//                         className="text-gray-500 hover:text-gray-700"
//                         title="Edit User"
//                     >
//                         <FontAwesomeIcon icon={faEdit} />
//                     </button>
//                     <button
//                         onClick={() => handleRemove('user', row.UserId)}
//                         className="text-red-500 hover:text-red-700"
//                         title="Remove User From Group"
//                     >
//                         <FontAwesomeIcon icon={faTrashAlt} />
//                     </button>
//                 </div>
//             )
//         }
//     ], [handleUserEdit]);

//     const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
//         if (!isOpen) return null;

//         return (
//             <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-10">
//                 <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//                     <h3 className="text-lg font-medium text-gray-900">Confirm Removal</h3>
//                     <p className="text-gray-500 my-4">{message}</p>
//                     <div className="flex justify-end gap-4">
//                         <button className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded" onClick={onClose}>No</button>
//                         <button className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded" onClick={onConfirm}>Yes</button>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     if (isInitializing) {
//         return <Spinner />;
//     }

//     return (
//         <div className="mobile-container mx-auto px-4 sm:px-6 lg:px-8">
//             <MobileHeader theme={theme} />
//             <ConfirmationModal
//                 isOpen={showConfirmationModal}
//                 onClose={() => setShowConfirmationModal(false)}
//                 onConfirm={() => confirmRemoval(removalType, removalIdentifier)}
//                 message={confirmationMessage}
//             />

//             {/* <PageHeader
//                 heading="Account Details"
//                 context="Edit account details."
//             /> */}

// <div className="text-end mb-4">
//         <button
//           type="button"
//           onClick={() => navigate(-1)}
//           className="text-gray-700 text-sm mr-4"
//         >
//           <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
//           Go Back
//         </button>
//       </div>

//              {/* New Details Section */}
//              <div className="bg-white shadow rounded-lg p-6 mb-8">
//             <h1 className="font-bold mb-4">Account Details:</h1>
//                 <h3 className="text-xl font-bold mb-4">{cardGroup.cardGroup.groupName}</h3>
//                 <p className="text-gray-600 mb-4">{cardGroup.cardGroup.description}</p>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Account Balance</h3>
//                         <p className="text-gray-600">${cardGroup.cardGroup.AccountBalance}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Credit Limit</h3>
//                         <p className="text-gray-600">${cardGroup.cardGroup.CreditLimit}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Pre-Pay Account</h3>
//                         <p className="text-gray-600">{cardGroup.cardGroup.isPrePay ? 'Yes' : 'No'}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Account Number</h3>
//                         <p className="text-gray-600">{cardGroup.cardGroup.AccountNumber}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Maintain Balance Amount</h3>
//                         <p className="text-gray-600">${cardGroup.cardGroup?.MinFunds || '0'}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Auto Reload</h3>
//                         <p className="text-gray-600">{cardGroup.cardGroup.MaintainBalance ? 'Yes' : 'No'}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Setup Complete</h3>
//                         <p className="text-gray-600">{cardGroup.cardGroup.SetupComplete ? 'Yes' : 'No'}</p>
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Subfleet ID</h3>
//                         <p className="text-gray-600">{cardGroup.cardGroup.SubfleetId || 'N/A'}</p>
//                     </div>
//                 </div>
//             </div>

//             {loading && <Spinner />}
            
//             {alertMessage && (
//                 <div className={`alert p-4 rounded ${alertMessage.startsWith('Successfully') ? 'bg-green-200 text-green-600' : 'bg-red-200 text-red-600'} flex justify-between items-center`}>
//                     {alertMessage}
//                     <button onClick={clearAlert} className="text-lg font-bold">×</button>
//                 </div>
//             )}
           
//             <form onSubmit={handleSubmit} className="space-y-2 mb-8">
//                 <div>
//                     <label className="block text-sm font-medium text-gray-700">Name</label>
//                     <input type="text" name="groupName" value={cardGroup?.cardGroup?.groupName || ''} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded-md" />
//                 </div>
//                 <div>
//                     <label className="block text-sm font-medium text-gray-700">Description</label>
//                     <input type="text" name="description" value={cardGroup?.cardGroup?.description || ''} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded-md" />
//                 </div>
//                 {(currentUser.isAdmin || currentUser.isContractAdmin) && (
//                 <div>
//                     <label className="block text-sm font-medium text-gray-700">Policy Number</label>
//                     <select
//                         id="PolicyNumber"
//                         name="PolicyNumber"
//                         value={policyNumber || ''}
//                         onChange={handleInputChange}
//                         className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
//                         required
//                     >
//                         <option value="">Select A Policy</option>
//                         {policies.map(policy => (
//                             <option key={policy.PolicyNumber} value={String(policy.PolicyNumber)}>
//                                 {policy.PolicyNumber} - {policy.Description}
//                             </option>
//                         ))}
//                     </select>
//                 </div>
//                 )}
//                 <div>
//                     <label className="block text-sm font-medium text-gray-700">Subfleet ID</label>
//                     <p className="text-xs text-gray-500 mt-1">
//                         The Subfleet ID is applied to transactions and used for detailed reporting. You can use a unique value or the account name, but a value is required. All cards assigned to this account will inherit this value.
//                     </p>
//                     <input
//                         type="text"
//                         name="SubfleetId"
//                         value={subfleetId || ''}
//                         onChange={handleInputChange}
//                         className="mt-1 p-2 w-full border rounded-md"
//                         maxLength="24"
//                         required
//                     />
//                 </div>
//                 <button type="submit" disabled={loading} className="themed-button">
//                     Update
//                 </button>
//             </form>
//             {successMessage && (
//                 <div className={`alert p-4 rounded bg-green-200 text-green-600 flex justify-between items-center`}>
//                     {successMessage}
//                     <button onClick={clearSuccessMessage} className="text-lg font-bold">×</button>
//                 </div>
//             )}

//             <div className="flex flex-col mb-8">
//                 <div className="flex justify-end mb-2">
//                     <button onClick={toggleCardModal} className="themed-button py-2 px-4 rounded">
//                         Add Cards
//                     </button>
//                 </div>
//                 <SubTableV2
//                     columns={cardColumns}
//                     title="Cards"
//                     data={cardDataOriginal}
//                     loading={loading}
//                     clearAlert={clearCardAlert}
//                     alertMessage={cardTableAlert}

//                 />
//             </div>
//             <div className="flex flex-col mb-8">
//                 <div className="flex justify-end mb-2">
//                     <button onClick={toggleUserModal} className="themed-button py-2 px-4 rounded">
//                         Add Users
//                     </button>
//                 </div>
//                 <SubTableV2
//                     columns={userTenantColumns}
//                     title="Users"
//                     data={userTenantMappings}
//                     loading={loading}
//                     clearAlert={clearUserAlert}
//                     alertMessage={userTableAlert}
//                 />
//             </div>
//             <CardGroupsAddCardModal
//                 isOpen={showAddCardModal}
//                 onClose={() => setShowAddCardModal(false)}
//                 cardGroupId={cardGroupNumber}
//                 onCardsAdded={onCardsAdded}
//             />
//             <AddUserModal
//                 isOpen={showAddUserModal}
//                 onClose={() => setShowAddUserModal(false)}
//                 cardGroupId={cardGroupNumber}
//                 onUsersAdded={onUsersAdded}
//             />
//         </div>
//     );
// };

// export default EditCardGroup;
